// src/presentation/components/DeletePatientDialog.tsx

import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

import { useDeletePatientMutation } from "../../infrastructure/api/patientsApi";
import { useNavigate } from "react-router-dom";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string | null;
  patientName?: string;
  onSuccess?: () => void;
}

export const DeletePatientDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  patientId,
  patientName,
  onSuccess,
}) => {
  const { t } = useTranslation("patients");
  const [deletePatient, { isLoading }] = useDeletePatientMutation();
  const navigate = useNavigate();
  const handleDelete = async () => {
    if (!patientId) {
      toast.error(t("deletePatientDialog.idMissing"));
      return;
    }

    try {
      await deletePatient(patientId).unwrap();
      toast.success(t("deletePatientDialog.success"));
      onSuccess?.();
      navigate("/patients");
      onOpenChange(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast.error(err?.data?.message || t("deletePatientDialog.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("deletePatientDialog.title")}
          </DialogTitle>

          <p className="text-sm text-gray-500 mt-1">
            {t("deletePatientDialog.confirm", { name: patientName })}
          </p>
        </DialogHeader>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="px-5 cursor-pointer"
          >
            {t("deletePatientDialog.goBack")}
          </Button>

          <Button
            onClick={handleDelete}
            loading={isLoading}
            className="bg-destructive hover:bg-destructive text-white px-5 cursor-pointer"
          >
            {t("deletePatientDialog.delete")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
